import * as translator from "../../../utils/translator";
import { ThankyouDocuments } from "./useThankyouDocuments";
import checkBadge from "../../../icons/ThankyouUkWeightLoss/check-badge.svg";
import backgroundSwoosh from "../../../icons/ThankyouUkWeightLoss/background-swoosh.svg";
import infoIcon from "../../../icons/ThankyouUkWeightLoss/info.svg";
import bodyScanIcon from "../../../icons/ThankyouUkWeightLoss/body-scan.svg";
import idDocumentIcon from "../../../icons/ThankyouUkWeightLoss/id-document.svg";
import stepDoneIcon from "../../../icons/ThankyouUkWeightLoss/step-done.svg";
import stepCurrentIcon from "../../../icons/ThankyouUkWeightLoss/step-current.svg";
import stepArrow from "../../../icons/ThankyouUkWeightLoss/step-arrow.svg";
import divider from "../../../icons/ThankyouUkWeightLoss/divider.svg";
import "./ThankyouUkWeightLoss.css";

interface Props {
    language?: string;
    country?: string;
    hashID?: string;
    documents: ThankyouDocuments;
    onUploadClick: () => void;
}

const STEPS = [
    { key: "step1", icon: stepDoneIcon },
    { key: "step2", icon: stepCurrentIcon },
    { key: "step3", icon: stepCurrentIcon },
];

// HL-12639: thank-you page for UK weight-loss orders. Same logic as before (the documents card shows
// only while a verification task is outstanding), V3 design.
export default function ThankyouUkWeightLoss({ language, country, hashID, documents, onUploadClick }: Props) {
    const t = (key: string) => translator.translate(language, "thankyou", `uk_wl/${key}`);

    const { needsPhoto, needsId } = documents;
    const hasBoth = needsPhoto && needsId;
    const hasCard = needsPhoto || needsId;
    const cardTitle = hasBoth ? t("card_title_both") : needsPhoto ? t("card_title_photo_only") : t("card_title_id_only");
    const items = [
        ...(needsPhoto ? [{ key: "photo", icon: bodyScanIcon }] : []),
        ...(needsId ? [{ key: "id", icon: idDocumentIcon }] : []),
    ];

    return (
        <>
            {/* No site header: the Figma frames start with the check badge. */}
            <div className="ty-ukwl">
                <img src={backgroundSwoosh} alt="" className="ty-ukwl__swoosh ty-ukwl__swoosh--top" />
                <img src={backgroundSwoosh} alt="" className="ty-ukwl__swoosh ty-ukwl__swoosh--bottom" />

                <div className="ty-ukwl__content">
                    <div className="ty-ukwl__badge">
                        <img src={checkBadge} alt="" className="ty-ukwl__badge-img" />
                    </div>

                    <div className="ty-ukwl__hero">
                        {/* Mobile frames with one document or none use the short title (Figma). */}
                        <h1 className={`ty-ukwl__title${hasBoth ? "" : " ty-ukwl__title--short-on-mobile"}`}>
                            {t("title")}
                            <span className="ty-ukwl__title-rest">{t("title_rest")}</span>
                        </h1>
                        <p className="ty-ukwl__subtitle">{t("subtitle")}</p>
                    </div>

                    {hasCard && (
                        <div className="ty-ukwl__card" data-testid="ty-ukwl-documents-card">
                            <div className="ty-ukwl__card-header">
                                <div className="ty-ukwl__card-title-row">
                                    <img src={infoIcon} alt="" className="ty-ukwl__card-title-icon" />
                                    <p className="ty-ukwl__card-title">{cardTitle}</p>
                                </div>
                                <p className="ty-ukwl__card-text">
                                    {t("card_text")}
                                    <strong>{t("card_text_bold")}</strong>
                                </p>
                            </div>

                            <div className="ty-ukwl__items">
                                {items.map((item, index) => (
                                    <div className="ty-ukwl__item" key={item.key}>
                                        <div className="ty-ukwl__item-icon-box">
                                            <img src={item.icon} alt="" className="ty-ukwl__item-icon" />
                                        </div>
                                        <div className="ty-ukwl__item-text">
                                            <p className="ty-ukwl__item-title">{index + 1}. {t(`${item.key}_item_title`)}</p>
                                            <p className="ty-ukwl__item-desc">{t(`${item.key}_item_desc`)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <a
                                href={`/${country}/account/my-orders/${hashID}/details`}
                                className="ty-ukwl__upload-btn"
                                onClick={onUploadClick}
                            >
                                {t("upload_btn")}
                                <span className="ty-ukwl__upload-btn-arrow"> →</span>
                            </a>
                        </div>
                    )}

                    <div className="ty-ukwl__steps">
                        <h2 className="ty-ukwl__steps-title">{t("steps_title")}</h2>
                        <div className="ty-ukwl__timeline">
                            {STEPS.map((step, index) => (
                                <div key={step.key}>
                                    {index > 0 && (
                                        <div className="ty-ukwl__step-connector">
                                            <img src={stepArrow} alt="" className="ty-ukwl__step-arrow" />
                                        </div>
                                    )}
                                    <div className="ty-ukwl__step">
                                        <img src={step.icon} alt="" className="ty-ukwl__step-icon" />
                                        <div className="ty-ukwl__step-text">
                                            <p className="ty-ukwl__step-title">{t(`${step.key}_title`)}</p>
                                            <p className="ty-ukwl__step-desc">{t(`${step.key}_desc`)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <img src={divider} alt="" className="ty-ukwl__divider" />
                    <p className="ty-ukwl__footer">{t("footer")}</p>
                </div>
            </div>
        </>
    );
}
